import { computed, observable, runInAction, makeObservable } from 'mobx'
import { Observable } from 'rxjs'
import { distinctUntilChanged, take } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import { filterNonNil, shareLatest } from 'utils/rx-util'
import MemberStore from './MemberStore'
import Member from './models/Member'
import User from './models/User'
import Workspace from './models/Workspace'
import WorkspaceStore from './WorkspaceStore'

/**
 * Getter for the Current User.
 */
export type GetCurrentUser = () => User

/**
 * Getter for the Current Workspace.
 */
export type GetCurrentWorkspace = () => Workspace

/**
 * Getter for the Current Workspace Member.
 */
export type GetCurrentMember = (workspaceId?: Id) => Member

/**
 * Tracks session information such as the current user and workspace.
 */
export default class SessionStore {
  /**
   * Current workspace.
   */
  @observable private _workspace: Workspace | null = null

  /**
   * Rx Observable for when the user is logged in. Subscribing after being logged in will
   * emit the current user immediately.
   */
  currentUser$: Observable<User>

  /**
   * Rx Observable for when a workspace has been selected. Emits the current user's workspace member in that workspace.
   * Subscribing after the fact will emit the current member immediately.
   */
  currentMember$: Observable<Member>

  /**
   *
   * @param getCurrentUserInternal gets the current user ID, or null if not authenticated.
   * @param workspaceStore
   * @param memberStore
   */
  constructor(
    private getCurrentUserInternal: () => User | null,
    private workspaceStore: Pick<WorkspaceStore, 'fetch'>,
    private memberStore: Pick<MemberStore, 'fetch' | 'getMember'>
  ) {
    makeObservable(this)
    this.currentUser$ = toStream(() => getCurrentUserInternal(), true).pipe(
      filterNonNil(),
      shareLatest(),
      take(1) // Only fire it once per subscriber.
    )
    this.currentMember$ = toStream(() => this._member, true).pipe(
      filterNonNil(),
      shareLatest(),
      distinctUntilChanged()
    )
  }

  /**
   * Determines whether you are authenticated.
   */
  @computed
  get isAuthenticated() {
    return !!this.getCurrentUserInternal()
  }

  /**
   * Gets the currently authenticated user, or throws if unauthenticated.
   * This store does not manage the current user, but it exposes it.
   */
  @computed
  get user() {
    const user = this.getCurrentUserInternal()
    if (user) return user
    else throw new Error('You are not authenticated.')
  }

  /**
   * Gets the currently authenticated users connection name by
   * parsing it from the id
   */
  @computed
  get enterpriseConnection() {
    const user = this.getCurrentUserInternal()
    if (!user || !user.id) throw new Error('You are not authenticated.')
    const parts = user.id.split('|')
    return parts.length === 3 ? parts[1] : null
  }

  /**
   * Gets the currently selected workspace, or throws if none is selected.
   */
  @computed
  get workspace() {
    if (this._workspace) return this._workspace
    else throw new Error('You are currently not on a Workspace.')
  }

  @computed
  get hasWorkspaceSelected() {
    return !!this._workspace
  }

  /**
   * Used for getting the current workspace member without throwing.
   */
  @computed
  private get _member() {
    const user = this.getCurrentUserInternal()
    const workspace = this._workspace
    if (!user || !workspace) {
      return null
    }
    return this.memberStore.getMember(workspace.id, user.id)
  }

  /**
   * The current workspace member.
   */
  @computed
  get member() {
    const member = this._member

    if (!member) {
      throw new Error(
        'There is no workspace member record for the currently logged-in user and selected workspace.'
      )
    }
    return member
  }

  /**
   * Determines if user is using an external identity provider to log out
   */
  get isFederatedLogout() {
    return Boolean(
      this.workspace.config.signup?.federated_logout &&
        this.user.authId?.toLocaleLowerCase().startsWith('samlp|')
    )
  }

  /**
   * Gets the current member. If a Workspace ID is specified, returns the member for that workspace.
   *
   * @param workspaceId
   */
  getCurrentMember(workspaceId?: Id) {
    if (!workspaceId) {
      return this.member
    }
    const found = this.memberStore.getMember(workspaceId, this.member.user.id)
    if (!found) {
      throw new Error(
        'There is no workspace member record for the currently logged-in user and requested workspace.'
      )
    }
    return found
  }

  /**
   * Selects the specified workspace.
   *
   * @param {*} idOrObj
   */
  async selectWorkspace(idOrObj: Workspace | Id) {
    const id = Id.assert(idOrObj instanceof Workspace ? idOrObj.id : idOrObj)
    const [workspace] = await Promise.all([
      this.workspaceStore.fetch(id),
      this.memberStore.fetch({
        workspace_id: id,
        user_id: Id.assert(this.user.id),
        user_public_id: this.user.publicId,
      }),
    ])

    runInAction(() => {
      this._workspace = workspace
    })
  }
}
